import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import Swal from 'sweetalert2';
import memberAPI from '../../middleware/memberAPI';
import PaperLinkScanner from '../../components/Bonus/PaperLinkScanner';
import { USER_PAYING_MEMBER_COUPON_TYPE } from '../../data/common';
import { alertError } from '../../Util/Util';
import RestoreIcon from '@material-ui/icons/Restore';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: '550px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  infoSection: {
    marginBottom: theme.spacing(3),
  },
  memberHeader: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  memberName: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  planName: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
  statusGrid: {
    marginTop: theme.spacing(2),
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  statusLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actionSection: {
    marginTop: theme.spacing(3),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  redTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    margin: theme.spacing(2, 0),
    color: '#ff4e23',
    textAlign: 'center',
  },
}));

const UserPayingMemberCouponModal = ({
  open,
  userPayingMember,
  onClose,
  onSuccess,
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [claimType, setClaimType] = useState();
  const [isConsignment, setIsConsignment] = useState(false);

  // 重置狀態當 modal 關閉或打開時
  useEffect(() => {
    if (open) {
      setClaimType(undefined);
      setIsConsignment(false);
    }
  }, [open]);

  if (!userPayingMember) {
    return null;
  }

  const hasProductPackage =
    userPayingMember.payingMemberInfo &&
    userPayingMember.payingMemberInfo.productPackageId;

  const canRestore = userPayingMember.canRestore === true;
  const couponType = userPayingMember.couponType;
  const isUnclaimed = couponType === 'UNCLAIMED';

  // 處理返還套券
  const handleRestoreCoupon = () => {
    Swal.fire({
      title: '確認返還套券',
      text: '確定要將此會員的套券返還為未領券狀態嗎？',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '確定',
      confirmButtonColor: '#d33',
      cancelButtonText: '取消',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);

        memberAPI
          .restorePayingMemberClaimStatus(userPayingMember.id)
          .then(
            () => {
              Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                title: '返還成功',
                icon: 'success',
              });

              // 刷新列表並停留在 modal
              if (onSuccess) {
                onSuccess();
              }
            },
            (error) => {
              alertError(error);
            },
          )
          .then(() => {
            setIsLoading(false);
          });
      }
    });
  };

  // 處理領電子券
  const handleClaimDigital = () => {
    Swal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '發送',
      cancelButtonText: '取消',
      title: '確認發送電子券嗎？',
    }).then((rs) => {
      if (rs.value) {
        setIsLoading(true);

        memberAPI
          .claimDigitalCoupon(userPayingMember.id)
          .then(
            () => {
              Swal.fire({
                title: '領券完成',
                icon: 'success',
              });

              // 關閉 modal 並刷新列表
              if (onSuccess) {
                onSuccess();
              }
              onClose();
            },
            (error) => {
              alertError(error);
            },
          )
          .then(() => {
            setIsLoading(false);
          });
      }
    });
  };

  // 處理選擇領紙本券
  const handleSelectPaperClaim = () => {
    setClaimType('paper');
  };

  // 處理 isConsignment 變更
  const handleChangeIsConsignment = (checked) => {
    setIsConsignment(checked);
  };

  // 處理 loading 變更 (由 PaperLinkScanner 呼叫)
  const handleLoadingChange = (loading) => {
    setIsLoading(loading);
  };

  // 處理完成紙本券領取
  const handleCompletePaperClaim = () => {
    // 關閉 modal 並刷新列表
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      disableBackdropClick
      disableEscapeKeyDown>
      <DialogTitle>轉換領券</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* 資訊顯示區 */}
        <Box className={classes.infoSection}>
          {/* 會員基本資訊 */}
          <Box className={classes.memberHeader}>
            <div className={classes.memberName}>
              {userPayingMember.userInfo.realName}
            </div>
            <div className={classes.planName}>
              {userPayingMember.payingMemberInfo
                ? userPayingMember.payingMemberInfo.name
                : '無方案'}
              {` - ID: ${userPayingMember.id}`}
            </div>
          </Box>

          {/* 狀態資訊列表 */}
          <Box>
            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>套券狀態</span>
              {hasProductPackage ? (
                <Chip label="含套券" color="primary" size="small" />
              ) : (
                <Chip label="不含套券" size="small" />
              )}
            </Box>

            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>轉換狀態</span>
              {canRestore ? (
                <Chip label="可以轉換" color="primary" size="small" />
              ) : (
                <Chip label="不可轉換" color="secondary" size="small" />
              )}
            </Box>

            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>領券狀態</span>
              <Chip
                label={USER_PAYING_MEMBER_COUPON_TYPE[couponType] || '未知'}
                color={isUnclaimed ? 'default' : 'primary'}
                size="small"
              />
            </Box>
          </Box>

          {/* 不可轉換的提示 */}
          {!canRestore && hasProductPackage && (
            <Alert severity="warning" style={{ marginTop: '16px' }}>
              此會員的套券已有使用記錄，無法進行轉換操作
            </Alert>
          )}
        </Box>

        {/* 操作區 */}
        {hasProductPackage && canRestore && (
          <Box className={classes.actionSection}>
            {/* 未領券狀態：顯示領券選項 */}
            {isUnclaimed && !claimType && (
              <>
                <div className={classes.redTitle}>
                  請選擇套券領取方式(只能擇一)
                </div>
                <Divider />
                <div className={classes.buttonGroup}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClaimDigital}
                    disabled={isLoading || !canRestore}
                    size="large">
                    領電子券
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSelectPaperClaim}
                    disabled={isLoading || !canRestore}
                    size="large">
                    領紙本券
                  </Button>
                </div>
              </>
            )}

            {/* 已領券狀態：顯示返還按鈕 */}
            {!isUnclaimed && !claimType && (
              <>
                <Alert severity="info" style={{ marginBottom: '16px' }}>
                  此會員已領取{' '}
                  <strong>{USER_PAYING_MEMBER_COUPON_TYPE[couponType]}</strong>
                  ，如需轉換請先返還套券。
                </Alert>
                <div className={classes.buttonGroup}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RestoreIcon />}
                    onClick={handleRestoreCoupon}
                    disabled={isLoading || !canRestore}
                    size="large">
                    返還套券
                  </Button>
                </div>
              </>
            )}

            {/* 領紙本券掃描介面 */}
            {claimType === 'paper' && (
              <div>
                <div className={classes.redTitle}>紙本券歸戶掃描</div>
                <Divider style={{ marginBottom: '16px' }} />
                <PaperLinkScanner
                  userId={userPayingMember.userInfo.id}
                  productPackageId={
                    userPayingMember.payingMemberInfo &&
                    userPayingMember.payingMemberInfo.productPackageId
                      ? userPayingMember.payingMemberInfo.productPackageId
                      : undefined
                  }
                  isConsignment={isConsignment}
                  onIsConsignmentChange={handleChangeIsConsignment}
                  userPayingMemberId={userPayingMember.id}
                  isLoading={isLoading}
                  onLoadingChange={handleLoadingChange}
                />
                <Alert severity="info" style={{ marginTop: '16px' }}>
                  請連續掃描所有紙本券，完成後點擊下方「完成領券」按鈕
                </Alert>
              </div>
            )}
          </Box>
        )}

        {/* 不含套券的提示 */}
        {!hasProductPackage && (
          <Alert severity="warning">此方案不含套券，無需進行領券操作。</Alert>
        )}
      </DialogContent>
      <DialogActions>
        {claimType === 'paper' ? (
          <>
            <Button
              onClick={() => setClaimType(undefined)}
              disabled={isLoading}
              color="default">
              返回
            </Button>
            <Button
              onClick={handleCompletePaperClaim}
              disabled={isLoading}
              color="primary"
              variant="contained">
              完成領券
            </Button>
          </>
        ) : (
          <Button onClick={onClose} disabled={isLoading} color="default">
            關閉
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

UserPayingMemberCouponModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userPayingMember: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

UserPayingMemberCouponModal.defaultProps = {
  userPayingMember: null,
  onSuccess: null,
};

export default UserPayingMemberCouponModal;
